import { useMemo } from "react"

import type { Issue, Project } from "@productlane/zero"

import { useZeroWorkspace } from "./zero"

/**
 * Returns the selected team IDs from the workspace settings.
 * Used to filter projects and issues by the teams enabled in Linear integration settings.
 */
export function useSelectedTeamIds(): readonly string[] {
	const workspace = useZeroWorkspace()
	const raw = workspace?.selectedTeamIds
	return useMemo(() => {
		if (!raw) return []
		if (Array.isArray(raw)) return raw
		return Object.entries(raw as Record<string, boolean>)
			.filter(([, v]) => v)
			.map(([k]) => k)
	}, [raw])
}

/**
 * Filters projects by selected teams.
 * A project is included if any of its linearTeamIds matches a selected team.
 * If no teams are selected, all projects are returned.
 */
export function useFilteredProjects<T extends Pick<Project, "linearTeamIds">>(
	projects: readonly T[],
): readonly T[] {
	const selectedTeamIds = useSelectedTeamIds()

	return useMemo(() => {
		if (selectedTeamIds.length === 0) return projects
		return projects.filter((project) =>
			project.linearTeamIds?.some((teamId) => selectedTeamIds.includes(teamId)),
		)
	}, [projects, selectedTeamIds])
}

/**
 * Filters issues by selected teams.
 * An issue is included if its team matches a selected team.
 * If no teams are selected, all issues are returned.
 */
export function useFilteredIssues<T extends Pick<Issue, "team">>(
	issues: readonly T[],
): readonly T[] {
	const selectedTeamIds = useSelectedTeamIds()

	return useMemo(() => {
		if (selectedTeamIds.length === 0) return issues
		return issues.filter(
			(issue) => issue.team && selectedTeamIds.includes(issue.team),
		)
	}, [issues, selectedTeamIds])
}
